import { useEffect, useState, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";

// Pages
import Home from "./pages/Home";
import CoconSacre from "./pages/CoconSacre";
import CoconSacre2026 from "./pages/CoconSacre2026";
import TheStoryOfJeinar from "./pages/TheStoryOfJeinar";
import TheStoryOfJeinarValouse from "./pages/TheStoryOfJeinarValouse";
// CoconSacreChoix et CoconSacrePaiementComplet supprimés — fusionnés dans CoconSacre2026
import Valouse from "./pages/Valouse";
import FragmentsPassage from "./pages/valouse/FragmentsPassage";
import Welcome from "./pages/Welcome";
import WelcomeConfirmation from "./pages/WelcomeConfirmation";
import Verify from "./pages/Verify";
import Sanctuary from "./pages/Sanctuary";
import Section from "./pages/Section";
import ContentView from "./pages/ContentView";
import HomeTestimonials from "./pages/HomeTestimonials";
import Testimonials from "./pages/Testimonials";
import ValouseTestimonials from "./pages/ValouseTestimonials";
import ValouseVoyageImmersion from "./pages/ValouseVoyageImmersion";
import ValouseVoyageParcours from "./pages/ValouseVoyageParcours";
import ValouseExperience from "./pages/ValouseExperience";
import ValousePreinscription from "./pages/ValousePreinscription";
import ValouseEspaceConfirmation from "./pages/ValouseEspaceConfirmation";
import ValouseEspace from "./pages/ValouseEspace";
import CoconeuseValerieValouse from "./pages/valouse/CoconeuseValerieValouse";
import CoconeuseStephanieValouse from "./pages/valouse/CoconeuseStephanieValouse";
import CoconeuseHeatherValouse from "./pages/valouse/CoconeuseHeatherValouse";
import CoconeuseAmeDivaValouse from "./pages/valouse/CoconeuseAmeDivaValouse";
import CoconeuseDeborahValouse from "./pages/valouse/CoconeuseDeborahValouse";
import CoconeuseEmmaValouse from "./pages/valouse/CoconeuseEmmaValouse";
import CoconeuseMaiteValouse from "./pages/valouse/CoconeuseMaiteValouse";
import Inscription from "./pages/Inscription";
import Retreat2024 from "./pages/Retreat2024";
import Cocons2025Bali from "./pages/Cocons2025Bali";
import Cocons2025Valouse from "./pages/Cocons2025Valouse";
import Partages from "./pages/Partages";
import PourquoiCoconTransforme from "./pages/PourquoiCoconTransforme";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import CoconeuseValerie from "./pages/CoconeuseValerie";
import CoconeuseStephanie from "./pages/CoconeuseStephanie";
import CoconeuseHeather from "./pages/CoconeuseHeather";
import CoconeuseAmeDiva from "./pages/CoconeuseAmeDiva";
import CoconeuseDeborah from "./pages/CoconeuseDeborah";
import CoconeuseEmma from "./pages/CoconeuseEmma";
import CoconeuseMaite from "./pages/CoconeuseMaite";
import MesCoconeuses from "./pages/MesCoconeuses";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

// Music Context
export const MusicContext = createContext(null);

export const useMusic = () => {
  const context = useContext(MusicContext);
  if (!context) {
    throw new Error("useMusic must be used within MusicProvider");
  }
  return context;
};

const MusicProvider = ({ children }) => {
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const audioRef = useRef(null);
  const autoplayAttempted = useRef(false);

  // Attempt autoplay on mount; if browser blocks, start on first interaction
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || autoplayAttempted.current) return;
    autoplayAttempted.current = true;

    let cleanedUp = false;
    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      window.removeEventListener('click', startOnInteraction);
      window.removeEventListener('touchstart', startOnInteraction);
      window.removeEventListener('scroll', startOnInteraction, { passive: true });
    };

    const startOnInteraction = () => {
      audio.play()
        .then(() => setIsMusicPlaying(true))
        .catch(() => {});
      cleanup();
    };

    audio.volume = 1;
    audio.play()
      .then(() => setIsMusicPlaying(true))
      .catch(() => {
        // Browser blocked autoplay — start on first user interaction
        window.addEventListener('click', startOnInteraction);
        window.addEventListener('touchstart', startOnInteraction);
        window.addEventListener('scroll', startOnInteraction, { passive: true });
      });

    return cleanup;
  }, []);

  const playMusic = () => {
    const audio = audioRef.current;
    if (!audio || isMusicPlaying) return;
    audio.volume = 1;
    audio.play()
      .then(() => setIsMusicPlaying(true))
      .catch(() => {});
  };

  const handleToggleMusic = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!isMusicPlaying) {
      audio.volume = 1;
      audio
        .play()
        .then(() => {
          setIsMusicPlaying(true);
        })
        .catch((error) => {
          console.error("Erreur lors du lancement de la musique COCON:", error);
        });
    } else {
      const duration = 3000;
      const steps = 30;
      const stepTime = duration / steps;
      let currentStep = 0;
      const startVolume = audio.volume;

      const fadeInterval = setInterval(() => {
        currentStep += 1;
        const factor = 1 - currentStep / steps;
        audio.volume = Math.max(0, startVolume * factor);

        if (currentStep >= steps) {
          clearInterval(fadeInterval);
          audio.pause();
          // Ne pas remettre à zéro pour reprendre là où on s'est arrêté
          audio.volume = startVolume;
          setIsMusicPlaying(false);
        }
      }, stepTime);
    }
  };

  return (
    <MusicContext.Provider value={{ isMusicPlaying, handleToggleMusic, playMusic }}>
      <audio
        ref={audioRef}
        src="https://customer-assets.emergentagent.com/job_6603108a-de24-4f36-aea5-adcbc13cbf2a/artifacts/fe34la7m_cocon%20website.m4a"
        loop
      />
      {children}
    </MusicContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("cocon_token") || null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAuthenticated(true);
        } catch (e) {
          localStorage.removeItem("cocon_token");
          setToken(null);
          setIsAuthenticated(false);
        }
      }
      setIsLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = (newToken) => {
    localStorage.setItem("cocon_token", newToken);
    setToken(newToken);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem("cocon_token");
    setToken(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ token, isAuthenticated, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/", { state: { from: location } });
    }
  }, [isAuthenticated, isLoading, navigate, location]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
        <div className="animate-breathing">
          <div className="w-16 h-16 rounded-full border-2 border-[#C5A059] flex items-center justify-center">
            <div className="w-8 h-8 rounded-full bg-[#C5A059]/20"></div>
          </div>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : null;
};

// Remonte en haut de la page à chaque changement de route (sauf si ancre hash)
const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      // Scroll to the hash element after a short delay for render
      setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  return null;
};

// Floating Music Button - avec traduction i18n
const FloatingMusicButton = () => {
  const { isMusicPlaying, handleToggleMusic } = useMusic();
  const { language } = useLanguage();
  
  const buttonText = isMusicPlaying 
    ? (language === 'en' ? 'Pause' : 'Pause') 
    : (language === 'en' ? 'Sound journey' : 'Voyage sonore');

  return (
    <button
      type="button"
      onClick={handleToggleMusic}
      data-testid="floating-music-btn"
      className="fixed bottom-6 left-6 z-50 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F3E5CF] text-[#3F2A24] text-[0.65rem] tracking-[0.15em] uppercase shadow-lg hover:bg-[#8C6A4A] hover:text-[#F3E5CF] transition-colors duration-300 border border-[#C5A059]/30"
    >
      <img
        src="https://customer-assets.emergentagent.com/job_react-revamp-2/artifacts/u0qtztc9_Voice%20Memo%20App%20Icon.jpg"
        alt="Musique COCON"
        className="w-5 h-5 object-contain rounded"
      />
      <span>{buttonText}</span>
    </button>
  );
};

function App() {
  return (
    <div className="App texture-overlay">
      <LanguageProvider>
      <MusicProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <FloatingMusicButton />
          <Routes>
            {/* NIVEAU 1 - Public: Landing générale COCON */}
            <Route path="/" element={<Home />} />
            
            {/* NIVEAU 2 - Public: Landing par retraite */}
            <Route path="/cocon-sacre-2026" element={<CoconSacre />} />
            <Route path="/cocon-sacre-2026/explorer" element={<CoconSacre2026 />} />
            <Route path="/story/jeinar" element={<TheStoryOfJeinar />} />
            <Route path="/valouse/story/jeinar" element={<TheStoryOfJeinarValouse />} />
            <Route path="/valouse/coconeuses/valerie" element={<CoconeuseValerieValouse />} />
            <Route path="/valouse/coconeuses/stephanie" element={<CoconeuseStephanieValouse />} />
            <Route path="/valouse/coconeuses/heather" element={<CoconeuseHeatherValouse />} />
            <Route path="/valouse/coconeuses/ame-diva" element={<CoconeuseAmeDivaValouse />} />
            <Route path="/valouse/coconeuses/deborah" element={<CoconeuseDeborahValouse />} />
            <Route path="/valouse/coconeuses/emma" element={<CoconeuseEmmaValouse />} />
            <Route path="/valouse/coconeuses/maite" element={<CoconeuseMaiteValouse />} />
            {/* /cocon-sacre-choix et /cocon-sacre-paiement-complet supprimés */}
            <Route path="/valouse" element={<Valouse />} />
            <Route path="/valouse/fragments" element={<FragmentsPassage />} />
            <Route path="/valouse/inscription" element={<Inscription />} />
            <Route path="/valouse/espace" element={<ValouseEspace />} />
            <Route path="/valouse/espace/confirmation" element={<ValouseEspaceConfirmation />} />
            <Route path="/valouse/voyage-immobile" element={<ValouseVoyageImmersion />} />
            <Route path="/valouse/voyage-deploie" element={<ValouseVoyageParcours />} />
            <Route path="/valouse/experience-cocon" element={<ValouseExperience />} />
            <Route path="/valouse/preinscription" element={<ValousePreinscription />} />
            <Route path="/temoignages" element={<HomeTestimonials />} />
            <Route path="/elles-en-parlent" element={<Testimonials />} />
            <Route path="/valouse/temoignages" element={<ValouseTestimonials />} />
            <Route path="/pourquoi-cocon-transforme" element={<PourquoiCoconTransforme />} />
            <Route path="/retraite-2024" element={<Retreat2024 />} />
            <Route path="/cocons-2025/bali" element={<Cocons2025Bali />} />
            <Route path="/cocons-2025/valouse" element={<Cocons2025Valouse />} />
            <Route path="/partages" element={<Partages />} />
            <Route path="/coconeuses/valerie" element={<CoconeuseValerie />} />
            <Route path="/coconeuses/heather" element={<CoconeuseHeather />} />
            <Route path="/coconeuses/ame-diva" element={<CoconeuseAmeDiva />} />
            <Route path="/coconeuses/deborah" element={<CoconeuseDeborah />} />
            <Route path="/coconeuses/emma" element={<CoconeuseEmma />} />
            <Route path="/coconeuses/maite" element={<CoconeuseMaite />} />
            <Route path="/coconeuses/stephanie" element={<CoconeuseStephanie />} />
            <Route path="/coconeuses" element={<MesCoconeuses />} />
            
            {/* NIVEAU 3 - Espace personnel (privé) */}
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/welcome/confirmation" element={<WelcomeConfirmation />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/sanctuary" element={
              <ProtectedRoute>
                <Sanctuary />
              </ProtectedRoute>
            } />
            <Route path="/section/:slug" element={
              <ProtectedRoute>
                <Section />
              </ProtectedRoute>
            } />
            <Route path="/content/:id" element={
              <ProtectedRoute>
                <ContentView />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      </MusicProvider>
      </LanguageProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
